import { z } from 'zod'

const text = z.string().trim().min(1).max(500)
export const warehouseDecimal = z.string().regex(/^\d+(\.\d{1,9})?$/)
export const warehousePositive = warehouseDecimal.refine((value) => /[1-9]/.test(value))
export const warehouseVersion = z.number().int().positive()
export const warehousePlace = z.object({ warehouse: text, location: text }).strict()
const conversion = z
  .object({ unit: text, factor: warehousePositive, note: z.string().optional() })
  .strict()
export const materialCreateSchema = z
  .object({
    name: text,
    grade: z.string().nullable(),
    category: text,
    unit: text,
    conversions: z.array(conversion),
    warehouse: text,
    location: text,
    critical: warehouseDecimal,
    supplier: z.string().trim(),
  })
  .strict()
export const materialUpdateSchema = materialCreateSchema
  .omit({ unit: true })
  .extend({ version: warehouseVersion, unitCost: warehouseDecimal })
  .strict()
export const criticalLevelSchema = z
  .object({ critical: warehouseDecimal, version: warehouseVersion })
  .strict()
export const warehouseFileSchema = z
  .object({
    mimeType: z.string().optional(),
    downloadUrl: z.string().optional(),
    id: text,
    name: text,
    sizeKb: z.number().nonnegative(),
    kind: z.string(),
    uploadedAt: z.string(),
    uploadedById: text,
  })
  .strict()
const operation = z.object({
  materialId: text,
  warehouse: text,
  location: text,
  version: warehouseVersion,
})
const entered = z.object({ quantity: warehousePositive, unit: text }).strict()
export const stockOperationSchema = z.discriminatedUnion('kind', [
  operation
    .extend({
      kind: z.literal('receipt'),
      quantity: warehousePositive,
      entered,
      unitCost: warehouseDecimal,
      ref: text,
      buyerEntityId: z.string().nullable().optional(),
      attachments: z.array(warehouseFileSchema).optional(),
    })
    .strict(),
  operation
    .extend({
      kind: z.literal('issue'),
      quantity: warehousePositive,
      entered,
      ref: text,
      reason: z.string().nullable(),
    })
    .strict(),
  operation
    .extend({
      kind: z.literal('writeoff'),
      quantity: warehousePositive,
      entered,
      ref: z.string().nullable(),
      reason: text,
    })
    .strict(),
  operation
    .extend({
      kind: z.literal('transfer'),
      quantity: warehousePositive,
      entered,
      to: warehousePlace,
      reason: z.string().nullable(),
    })
    .strict(),
  operation
    .extend({ kind: z.literal('adjustment'), counted: warehouseDecimal, reason: text })
    .strict(),
])
export const receiveExpectedSchema = warehousePlace
  .extend({
    quantity: warehousePositive,
    unit: text.optional(),
    document: text,
    version: warehouseVersion,
  })
  .strict()
export const issueKitSchema = z.object({ version: warehouseVersion }).strict()
export const renameWarehouseSchema = z
  .object({
    kind: z.enum(['location', 'category']),
    from: text,
    to: text,
    warehouse: z.string().optional(),
  })
  .strict()
export type MaterialCreateInput = z.infer<typeof materialCreateSchema>
export type MaterialUpdateInput = z.infer<typeof materialUpdateSchema>
export type StockOperationInput = z.infer<typeof stockOperationSchema>
